import { InputStepper, Tag } from '@/components';
import { Column, Element, Icon, Row } from '@vuddy/components';
import { isNonNil } from '@vuddy/utils';
import Image from 'next/image';
import * as styles from './bottom.css';
import type { ProductItemWithAdditionalInfo } from './useProductItemList';

interface PurchaseItemContentProps {
  item: ProductItemWithAdditionalInfo;
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
  hasVerificationCode?: boolean;
  verificationCodeName?: string;
  onCodeInputClick?: () => void;
}

export const PurchaseItemContent = ({
  item,
  quantity = 1,
  onQuantityChange,
  hasVerificationCode,
  verificationCodeName,
  onCodeInputClick,
}: PurchaseItemContentProps) => {
  const isPurchaseLimited =
    !item.isPurchasable && item.remainingPurchaseCount === 0;
  const disabled = item.saleStatus !== 'ON_SALE' || isPurchaseLimited;

  const handleStepperClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <Row flex={1} gap="gap_xl">
      <Column className={styles.orderItemDescription}>
        <Column style={{ gap: '2px' }}>
          <Row gap="gap_xs">
            <Element
              typography="bodyXsAccent"
              color={disabled ? 'disabled' : 'secondary'}>
              {item.name}
            </Element>
            {isNonNil(item.remainQuantity) && item.remainQuantity === 0 ? (
              <Tag label="품절" variant="gray" size="small" />
            ) : isPurchaseLimited ? (
              <Tag label="구매 제한" variant="gray" size="small" />
            ) : null}
          </Row>
          <Element
            typography="bodyXs"
            color={disabled ? 'disabled' : 'tertiary'}>
            {item.description}
          </Element>
          {hasVerificationCode && (
            <Row
              as="button"
              alignItems="center"
              gap="gap_2xs"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                onCodeInputClick?.();
              }}
              className={styles.verificationCodeButtonDigital}>
              <Icon name="FILL_PROMOTION_CODE" size="16px" color="secondary" />
              <Element
                typography="captionBaseAccent"
                color="secondary"
                className={
                  verificationCodeName
                    ? styles.verificationCodeText
                    : styles.verificationCodeTextUnderline
                }>
                {verificationCodeName || '코드 입력'}
              </Element>
            </Row>
          )}
        </Column>
        {item.type === 'PHYSICAL' && onQuantityChange && (
          <Element onClick={handleStepperClick} className={styles.stepper}>
            <InputStepper
              value={quantity}
              onChange={onQuantityChange}
              min={1}
              max={item.allowedMaxOrderCount ?? item.remainQuantity}
              disabled={disabled}
              tabIndex={-1}
            />
          </Element>
        )}
      </Column>
      <Column alignItems="flex-end" gap="gap_2xs">
        <Element
          typography="bodyBaseAccent"
          color={disabled ? 'disabled' : 'primary'}>
          {item.price.amount.toLocaleString()}원
        </Element>
        {item.pointBenefit && (
          <Row gap="gap_2xs" alignItems="center">
            {item.pointBenefit.type === 'SERIES_POINT' ? (
              <Icon name="ETC_POINT" size="16px" />
            ) : (
              <Image
                width={16}
                height={16}
                alt="포인트 icon"
                src="/images/icons/inish-ic-point.png"
              />
            )}
            <Element typography="captionBaseAccent" color="vuddyPrimary">
              {item.pointBenefit.text}
            </Element>
          </Row>
        )}
      </Column>
    </Row>
  );
};
